import React, { FormEventHandler, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Member } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { buttonClasses, inputClasses } from "../common/tailwindClassnames";

type MemberModalProps = {
    member: Member,
    existingMember: Member,
    onChange: Function,
    onSelectRole: Function,
    onToggleModal: MouseEventHandler,
    onSave: FormEventHandler,
    newMember: boolean,
    currentMember: Member,
    superuser: boolean,
    errors: any,
}

const MemberModal = ({
    member,
    existingMember,
    onChange,
    onSelectRole,
    onToggleModal,
    onSave,
    newMember,
    currentMember,
    superuser,
    errors
}: MemberModalProps) => {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 transition-colors duration-300"
                onClick={onToggleModal}></div>
            <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
                <div className="z-20 flex flex-col bg-white dark:bg-dark-gray w-full sm:w-5/6 md:w-3/4 lg:w-1/2 max-w-2xl shadow rounded-lg transition-colors duration-300">
                    <div className="h-full">
                        <div className="bg-white dark:bg-dark-gray z-30 sticky top-0 rounded-lg transition-colors duration-300">
                            <div className="flex flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={onToggleModal}
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-3 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-300"
                                    data-modal-hide="authentication-modal"
                                >
                                    <svg aria-hidden="true" className="w-7 h-7 text-off-black dark:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                    <span className="sr-only">{t("Close Modal")}</span>
                                </button>
                            </div>
                        </div>
                        <div className="px-6 pb-6 lg:px-8 h-full">
                            <div className="flex justify-between">
                                <h1 className="text-3xl font-semibold dark:text-white transition-colors duration-300">{(currentMember.role === "admin" || superuser) ? (newMember ? t("Add User") : t("Update User")) : t("User Details")}</h1>
                            </div>
                            <div onSubmit={onSave} className="h-full">
                                <div className="relative flex flex-col my-3 h-full justify-between">
                                    <div>
                                        {/* email, role, first name, last name */}
                                        <div className="flex flex-col w-1/2">
                                            <label className="dark:text-white transition-colors duration-300">{t("Email")}*</label>
                                            <input
                                                id="email"
                                                placeholder=""
                                                className={inputClasses.standard}
                                                value={newMember ? member.email : existingMember?.email}
                                                onChange={(e) => onChange(e)}
                                                disabled={currentMember.role === "viewer" && !superuser}>
                                            </input>
                                            {errors.email && <div className="flex flex-row text-orange-600 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.email}</p></div>}
                                        </div>
                                        <div className="flex flex-col w-1/2 my-2">
                                            <label className="mb-2 dark:text-white transition-colors duration-300">{t("Role")}*</label>
                                            <SelectInput
                                                options={[{
                                                    label: t("Admin"),
                                                    value: "admin"
                                                }, {
                                                    label: t("Viewer"),
                                                    value: "viewer"
                                                }]}
                                                className={"basic-single dark:text-white"}
                                                defaultValue={newMember ? {
                                                    label: t("Select"),
                                                    value: 0
                                                } : {
                                                    label: existingMember.role,
                                                    value: existingMember.role
                                                }}
                                                isDisabled={currentMember.role === "viewer" && !superuser}
                                                isLoading={false}
                                                isClearable={false}
                                                isSearchable={true}
                                                isMulti={false}
                                                name={"Type"}
                                                onChange={(e: any) => onSelectRole(e)}
                                            />
                                            {errors.role && <div className="flex flex-row text-orange-600 py-1 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.role}</p></div>}
                                        </div>
                                        <div className="flex flex-col w-1/2">
                                            <label className="dark:text-white transition-colors duration-300">{t("First Name")}*</label>
                                            <input
                                                id="first_name"
                                                placeholder=""
                                                className={inputClasses.standard}
                                                value={newMember ? member.first_name : existingMember.first_name}
                                                onChange={(e) => onChange(e)}
                                                disabled={currentMember.role === "viewer" && !superuser}>
                                            </input>
                                            {errors.first_name && <div className="flex flex-row text-orange-600 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.first_name}</p></div>}
                                        </div>
                                        <div className="flex flex-col w-1/2 my-2">
                                            <label className="dark:text-white transition-colors duration-300">{t("Last Name")}*</label>
                                            <input
                                                id="last_name"
                                                placeholder=""
                                                className={inputClasses.standard}
                                                value={newMember ? member.last_name : existingMember.last_name}
                                                onChange={(e) => onChange(e)}
                                                disabled={currentMember.role === "viewer" && !superuser}>
                                            </input>
                                            {errors.last_name && <div className="flex flex-row text-orange-600 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.last_name}</p></div>}
                                        </div>
                                    </div>
                                </div>
                                {/* submit and cancel button */}
                                {(currentMember.role === "admin" || superuser) && <div className="flex flex-row-reverse mt-2">
                                    <button
                                        className={buttonClasses.primary}
                                        type="submit"
                                        onClick={onSave}>
                                        {newMember ? t("Add") : t("Update")}
                                    </button>
                                    <button
                                        className={buttonClasses.secondary}
                                        type="button"
                                        onClick={onToggleModal}>
                                        {t("Cancel")}
                                    </button>
                                </div>}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MemberModal;